import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import EnvelopeStateBadge from "@/modules/racket_lab_admin/EnvelopeStateBadge";
import ConfirmActionDialog from "@/modules/racket_lab_admin/ConfirmActionDialog";
import NotificationEnvelopeForm from "@/modules/racket_lab_admin/NotificationEnvelopeForm";
import DispatchStatusBanner from "@/modules/racket_lab_admin/DispatchStatusBanner";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminEnvelopes — Sub-sprint C list + create-draft.
 */
const RLAdminEnvelopes = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTemplate, setFilterTemplate] = useState("");
  const [filterTicket, setFilterTicket] = useState("");
  const params = {};
  if (filterStatus) params.status = filterStatus;
  if (filterTemplate) params.template_key = filterTemplate;
  if (filterTicket) params.ticket_id = filterTicket;

  const { data, loading, error, reload } = useAdminApi("/admin/notification-envelopes", { params });
  const rows = data?.items || [];
  const { run, loading: mutating } = useAdminMutation();
  const [draft, setDraft] = useState({
    template_key: "racketLab.email.ready_for_pickup",
    channel: "email",
    recipient_user_id: "",
    language: "en",
    from_role: "shop",
    organisation_id: "TEAMSOFTWAREINNOVATIONS",
  });

  const onCreate = async () => {
    const body = { ...draft, variables: {}, app_context: "racket_lab" };
    if (!body.recipient_user_id?.trim()) return;
    const created = await run("post", "/admin/notification-envelopes", body);
    reload();
    if (created?.id) navigate(`/admin/racket-lab/notifications/envelopes/${created.id}`);
  };

  const columns = [
    { key: "id", label: t("common.columns.id"), render: (r) => (<code className="font-mono text-[10px]">{r.id?.slice(0, 12)}</code>) },
    { key: "template_key", label: t("notifications.envelopes.columns.template"), render: (r) => (<span className="text-xs">{r.template_key}</span>) },
    { key: "channel", label: t("notifications.envelopes.columns.channel"), render: (r) => (<span className="text-xs">{r.channel}</span>) },
    { key: "status", label: t("notifications.envelopes.columns.state"), render: (r) => (<EnvelopeStateBadge status={r.status} dataTestId={`rl-envelope-state-${r.id}`} />) },
    {
      key: "ticket_id",
      label: t("notifications.envelopes.columns.ticket"),
      render: (r) => (
        <span className="inline-flex items-center gap-1">
          <code className="font-mono text-[10px] text-slate-600">{r.ticket_id ? r.ticket_id.slice(0, 8) : "—"}</code>
          {(r.token_scope || []).includes("rl_ticket_status_trigger") && (
            <span
              data-testid={`rl-envelope-auto-badge-${r.id}`}
              className="rounded bg-indigo-100 px-1 py-0.5 text-[9px] font-medium text-indigo-700"
            >
              {t("notifications.envelopes.origin_auto")}
            </span>
          )}
        </span>
      ),
    },
    { key: "recipient_user_id", label: t("notifications.envelopes.columns.recipient"), render: (r) => (<code className="text-[10px] text-slate-500">{(r.recipient_user_id || "").slice(0, 12)}</code>) },
    { key: "created_at", label: t("notifications.envelopes.columns.created"), render: (r) => (<span className="text-[10px] text-slate-500">{r.created_at?.slice(0, 19).replace("T", " ") || "—"}</span>) },
  ];

  return (
    <div data-testid="rl-admin-envelopes-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <DispatchStatusBanner />

      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t("notifications.envelopes.page_header")}</h1>
          <p className="text-xs text-slate-500">{t("banners.pilot_mode_secondary")}</p>
        </div>
        <ConfirmActionDialog
          dataTestId="rl-envelope-create-confirm"
          trigger={
            <button
              data-testid="rl-envelopes-create-btn"
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              disabled={mutating}
            >
              <Plus className="w-3.5 h-3.5" /> {t("common.actions.create")}
            </button>
          }
          title={t("notifications.envelopes.create_title")}
          description={t("notifications.envelopes.create_title")}
          confirmLabel={t("common.actions.create")}
          onConfirm={onCreate}
        >
          <NotificationEnvelopeForm value={draft} onChange={setDraft} disabled={mutating} />
        </ConfirmActionDialog>
      </header>

      <div data-testid="rl-envelopes-filters" className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("notifications.envelopes.columns.state")}</span>
          <select
            data-testid="rl-envelopes-filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("common.filters.any_option")}</option>
            <option value="draft">draft</option>
            <option value="queued">queued</option>
            <option value="dispatched">dispatched</option>
            <option value="cancelled">cancelled</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("notifications.envelopes.columns.template")}</span>
          <input
            data-testid="rl-envelopes-filter-template"
            value={filterTemplate}
            onChange={(e) => setFilterTemplate(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
            placeholder={t("notifications.envelopes.filter_template_placeholder")}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("notifications.envelopes.columns.ticket")}</span>
          <input
            data-testid="rl-envelopes-filter-ticket"
            value={filterTicket}
            onChange={(e) => setFilterTicket(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
            placeholder={t("notifications.envelopes.filter_ticket_placeholder")}
          />
        </label>
        <span className="ml-auto text-[10px] text-slate-500">{t("common.list.counter_of_total", { count: rows.length, total: data?.total ?? "—" })}</span>
      </div>

      <AdminApiTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        emptyLabel={t("notifications.envelopes.empty")}
        onRowClick={(r) => navigate(`/admin/racket-lab/notifications/envelopes/${r.id}`)}
        dataTestId="rl-envelopes-table"
      />
    </div>
  );
};

export default RLAdminEnvelopes;
